import { useState, useEffect } from "react";
import Grid from "@mui/material/Grid";
import CircularProgress from "@mui/material/CircularProgress";
import { IoGitNetworkOutline } from "react-icons/io5";

import DashboardLayout from "components/layout/DashboardLayout";
import DashboardNavbar from "components/navigation/DashboardNavbar";
import VuiBox from "components/ui/VuiBox";
import VuiTypography from "components/ui/VuiTypography";
import VuiButton from "components/ui/VuiButton";
import MarkdownRenderer from "components/MarkdownRenderer";

import { useAnalytics } from "context/AnalyticsContext";
import { adminService } from "services/adminService";
import jwtService from "services/jwtService";

// ── Helpers ───────────────────────────────────────────────────────────────────
const MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

function formatPeriod(start, end) {
  if (!start || !end) return "";
  const s = new Date(start + "T12:00:00");
  const e = new Date(end + "T12:00:00");
  if (s.getFullYear() === e.getFullYear()) {
    return `${MONTHS[s.getMonth()]} – ${MONTHS[e.getMonth()]} ${e.getFullYear()}`;
  }
  return `${MONTHS[s.getMonth()]} ${s.getFullYear()} – ${MONTHS[e.getMonth()]} ${e.getFullYear()}`;
}

// ── Section config (WEB handled separately) ───────────────────────────────────
const SECTIONS = [
  { key: "treatmentLandscape", label: "Treatment Landscape",          color: "#34d399" },
  { key: "competitiveIntel",   label: "Competitive Intelligence",     color: "#f87171" },
  { key: "contentSignals",     label: "Content & Engagement Signals", color: "#fbbf24" },
];

// ── Sentiment colour mapping ──────────────────────────────────────────────────
const SENTIMENT_COLORS = {
  "Positive":      { color: "#34d399", bg: "rgba(52,211,153,0.12)",  border: "rgba(52,211,153,0.3)"   },
  "Neutral":       { color: "#fbbf24", bg: "rgba(251,191,36,0.12)",  border: "rgba(251,191,36,0.3)"   },
  "Negative":      { color: "#f87171", bg: "rgba(248,113,113,0.12)", border: "rgba(248,113,113,0.3)"  },
  "Not mentioned": { color: "#94a3b8", bg: "rgba(148,163,184,0.08)", border: "rgba(148,163,184,0.18)" },
  "Mentioned":     { color: "#34d399", bg: "rgba(52,211,153,0.12)",  border: "rgba(52,211,153,0.3)"   },
};
const neutralStyle = { color: "#e2e8f0", bg: "rgba(255,255,255,0.06)", border: "rgba(255,255,255,0.12)" };

// ── Minicard row ──────────────────────────────────────────────────────────────
function HighlightCards({ highlights }) {
  if (!highlights) return null;

  const cards = [
    { label: "Top Theme",          value: highlights.topTheme,          coloured: false },
    { label: "Popular Competitor", value: highlights.popularCompetitor, coloured: false },
    { label: "Top Engagement",     value: highlights.topEngagement,     coloured: false },
    { label: "Terumo Sentiment",   value: highlights.terumoSentiment,   coloured: true  },
    { label: "Terumo Presence",    value: highlights.terumoPresence,    coloured: true  },
  ];

  return (
    <VuiBox display="flex" flexWrap="wrap" gap={1.5} mb={2.5}>
      {cards.map(({ label, value, coloured }) => {
        const style = coloured ? (SENTIMENT_COLORS[value] || neutralStyle) : neutralStyle;
        return (
          <VuiBox
            key={label}
            sx={{
              flex: "1 1 150px",
              minWidth: 130,
              background: style.bg,
              border: `1px solid ${style.border}`,
              borderRadius: 2,
              px: 2,
              py: 1.75,
            }}
          >
            <VuiTypography
              sx={{
                display: "block",
                fontSize: "0.68rem",
                fontWeight: 600,
                textTransform: "uppercase",
                letterSpacing: "0.09em",
                color: "rgba(255,255,255,0.55)",
                mb: 0.75,
              }}
            >
              {label}
            </VuiTypography>
            <VuiTypography
              sx={{
                fontWeight: 700,
                fontSize: "0.95rem",
                color: coloured ? style.color : "#f1f5f9",
                lineHeight: 1.25,
              }}
            >
              {value || "—"}
            </VuiTypography>
          </VuiBox>
        );
      })}
    </VuiBox>
  );
}

// ── Consensus / Divergence cards ──────────────────────────────────────────────
function FieldOpinionCards({ consensus, divergence }) {
  const hasC = consensus && consensus.length > 0;
  const hasD = divergence && divergence.length > 0;
  if (!hasC && !hasD) return null;

  const Card = ({ title, bullets, accent, border, bg, dot }) => (
    <VuiBox sx={{ flex: "1 1 300px", background: bg, border: `1px solid ${border}`, borderRadius: 2, p: 2.5 }}>
      <VuiTypography sx={{ display: "block", fontWeight: 700, fontSize: "0.78rem", textTransform: "uppercase", letterSpacing: "0.09em", color: accent, mb: 1.5 }}>
        {title}
      </VuiTypography>
      <VuiBox component="ul" sx={{ m: 0, p: 0, listStyle: "none" }}>
        {bullets.map((b, i) => (
          <VuiBox key={i} component="li" display="flex" alignItems="flex-start" gap={1.25} sx={{ mb: i < bullets.length - 1 ? 1.25 : 0 }}>
            <VuiBox sx={{ width: 5, height: 5, borderRadius: "50%", background: dot, mt: "7px", flexShrink: 0 }} />
            <VuiTypography sx={{ fontSize: "0.8rem", lineHeight: 1.6, color: "rgba(255,255,255,0.82)" }}>
              {b}
            </VuiTypography>
          </VuiBox>
        ))}
      </VuiBox>
    </VuiBox>
  );

  return (
    <VuiBox display="flex" flexWrap="wrap" gap={2} mb={3}>
      {hasC && <Card title="Field Consensus" bullets={consensus} accent="#34d399" border="rgba(52,211,153,0.2)" bg="rgba(52,211,153,0.05)" dot="#34d399" />}
      {hasD && <Card title="Points of Debate" bullets={divergence} accent="#fbbf24" border="rgba(251,191,36,0.2)" bg="rgba(251,191,36,0.05)" dot="#fbbf24" />}
    </VuiBox>
  );
}

// ── Key Signals strip ─────────────────────────────────────────────────────────
function ExecSummaryStrip({ bullets }) {
  if (!bullets || bullets.length === 0) return null;
  return (
    <VuiBox mb={3} sx={{ background: "rgba(167,139,250,0.07)", border: "1px solid rgba(167,139,250,0.18)", borderRadius: 2, p: 2.5 }}>
      <VuiTypography sx={{ display: "block", fontSize: "0.78rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.09em", color: "#a78bfa", mb: 1.5 }}>
        Key Signals
      </VuiTypography>
      <VuiBox display="flex" flexWrap="wrap" gap={1.5}>
        {bullets.map((b, i) => (
          <VuiBox
            key={i}
            sx={{
              background: "rgba(167,139,250,0.1)",
              border: "1px solid rgba(167,139,250,0.22)",
              borderLeft: "3px solid #a78bfa",
              borderRadius: 1.5,
              px: 2,
              py: 1.25,
              flex: "1 1 280px",
              minWidth: 0,
            }}
          >
            <VuiTypography sx={{ fontSize: "0.875rem", lineHeight: 1.6, color: "#f1f5f9" }}>
              {b}
            </VuiTypography>
          </VuiBox>
        ))}
      </VuiBox>
    </VuiBox>
  );
}

// ── Narrative section card ────────────────────────────────────────────────────
function SectionCard({ label, content, accentColor }) {
  return (
    <VuiBox
      sx={{
        background: "rgba(255,255,255,0.03)",
        border: "1px solid rgba(255,255,255,0.09)",
        borderTop: `3px solid ${accentColor}`,
        borderRadius: 2,
        p: 3,
        mb: 2,
      }}
    >
      <VuiTypography sx={{ display: "block", fontSize: "0.78rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.09em", color: accentColor, mb: 1.75 }}>
        {label}
      </VuiTypography>
      {content
        ? <MarkdownRenderer markdownText={content} />
        : <VuiTypography sx={{ fontSize: "0.875rem", color: "rgba(255,255,255,0.4)", fontStyle: "italic" }}>No data captured for this section this period.</VuiTypography>
      }
    </VuiBox>
  );
}

// ── WEB elevated card ─────────────────────────────────────────────────────────
const WEB_COLOR = "#60a5fa";
const DIRECTION_STYLES = {
  "strengthening": { color: "#34d399", label: "↑ Strengthening" },
  "stable":        { color: "#fbbf24", label: "→ Stable"         },
  "under pressure":{ color: "#f87171", label: "↓ Under pressure" },
};

function WebPerceptionCard({ content }) {
  const lower = (content || "").toLowerCase();
  const dirKey = Object.keys(DIRECTION_STYLES).find(k => lower.includes(k));
  const dir = dirKey ? DIRECTION_STYLES[dirKey] : null;

  return (
    <VuiBox sx={{ background: "rgba(96,165,250,0.05)", border: "1px solid rgba(96,165,250,0.18)", borderTop: "3px solid #60a5fa", borderRadius: 2, p: 3, mb: 2 }}>
      <VuiBox display="flex" justifyContent="space-between" alignItems="center" mb={1.75}>
        <VuiTypography sx={{ fontSize: "0.78rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.09em", color: WEB_COLOR }}>
          WEB Device Perception
        </VuiTypography>
        {dir && (
          <VuiBox sx={{ background: `${dir.color}15`, border: `1px solid ${dir.color}40`, borderRadius: 1, px: 1.5, py: 0.5 }}>
            <VuiTypography sx={{ fontSize: "0.75rem", fontWeight: 700, color: dir.color }}>
              {dir.label}
            </VuiTypography>
          </VuiBox>
        )}
      </VuiBox>
      {content
        ? <MarkdownRenderer markdownText={content} />
        : <VuiTypography sx={{ fontSize: "0.875rem", color: "rgba(255,255,255,0.4)", fontStyle: "italic" }}>No data captured for this section this period.</VuiTypography>
      }
    </VuiBox>
  );
}

// ── Recommended Actions card ──────────────────────────────────────────────────
function RecommendedActionsCard({ actions }) {
  if (!actions || actions.length === 0) return null;
  return (
    <VuiBox sx={{ background: "rgba(167,139,250,0.05)", border: "1px solid rgba(167,139,250,0.18)", borderTop: "3px solid #a78bfa", borderRadius: 2, p: 3, flex: "1 1 300px" }}>
      <VuiTypography sx={{ display: "block", fontSize: "0.78rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.09em", color: "#a78bfa", mb: 1.75 }}>
        Recommended Actions
      </VuiTypography>
      <VuiBox component="ul" sx={{ m: 0, p: 0, listStyle: "none" }}>
        {actions.map((a, i) => (
          <VuiBox
            key={i}
            component="li"
            sx={{ borderLeft: "2px solid rgba(167,139,250,0.35)", pl: 1.75, mb: i < actions.length - 1 ? 1.75 : 0 }}
          >
            <MarkdownRenderer markdownText={a} />
          </VuiBox>
        ))}
      </VuiBox>
    </VuiBox>
  );
}

// ── Scale to unlock section ───────────────────────────────────────────────────
const LOCKED_CHARTS = [
  {
    title: "KOL Share of Voice",
    description: "Track what % of organic physician product mentions belong to WEB vs. every competitor — updated each period.",
    bars: [35, 55, 42, 68, 28, 50, 72, 38],
  },
  {
    title: "Topic Momentum",
    description: "See which clinical themes are accelerating or fading across your physician panel week over week.",
    bars: [20, 30, 45, 38, 55, 62, 70, 65],
  },
  {
    title: "Competitor Velocity",
    description: "Rate of clinical evidence generation per competitor — who is building the fastest evidence base.",
    bars: [60, 45, 70, 38, 55, 42, 65, 50],
  },
];

function LockedChartsRow() {
  return (
    <VuiBox mb={3}>
      <VuiBox display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <VuiBox>
          <VuiTypography sx={{ fontSize: "0.85rem", fontWeight: 700, color: "#f1f5f9", display: "block", mb: 0.25 }}>
            Advanced Intelligence
          </VuiTypography>
          <VuiTypography sx={{ fontSize: "0.75rem", color: "rgba(255,255,255,0.5)" }}>
            Available when your KOL panel scales to 25+ physicians
          </VuiTypography>
        </VuiBox>
        <VuiBox sx={{ background: "rgba(251,191,36,0.1)", border: "1px solid rgba(251,191,36,0.25)", borderRadius: 1.5, px: 2, py: 0.75 }}>
          <VuiTypography sx={{ fontSize: "0.72rem", fontWeight: 700, color: "#fbbf24", letterSpacing: "0.05em" }}>
            🔒 Requires Larger Panel
          </VuiTypography>
        </VuiBox>
      </VuiBox>

      <VuiBox display="flex" gap={2} flexWrap="wrap">
        {LOCKED_CHARTS.map(({ title, description, bars }) => (
          <VuiBox
            key={title}
            sx={{
              flex: "1 1 220px",
              background: "rgba(255,255,255,0.03)",
              border: "1px solid rgba(255,255,255,0.08)",
              borderRadius: 2,
              p: 2.5,
              position: "relative",
              overflow: "hidden",
              minHeight: 160,
            }}
          >
            {/* Abstract bar chart hint */}
            <VuiBox
              sx={{
                position: "absolute",
                bottom: 0,
                left: 0,
                right: 0,
                height: "55%",
                display: "flex",
                alignItems: "flex-end",
                gap: "3px",
                px: 2,
                pb: 0,
                pointerEvents: "none",
                filter: "blur(1px)",
                opacity: 0.18,
              }}
            >
              {bars.map((h, i) => (
                <VuiBox
                  key={i}
                  sx={{
                    flex: 1,
                    height: `${h}%`,
                    background: "linear-gradient(to top, rgba(96,165,250,0.8), rgba(167,139,250,0.4))",
                    borderRadius: "2px 2px 0 0",
                  }}
                />
              ))}
            </VuiBox>

            {/* Content */}
            <VuiBox sx={{ position: "relative", zIndex: 1 }}>
              <VuiTypography sx={{ fontSize: "0.85rem", fontWeight: 700, color: "rgba(255,255,255,0.65)", mb: 0.75 }}>
                {title}
              </VuiTypography>
              <VuiTypography sx={{ fontSize: "0.75rem", color: "rgba(255,255,255,0.38)", lineHeight: 1.55 }}>
                {description}
              </VuiTypography>
            </VuiBox>
          </VuiBox>
        ))}
      </VuiBox>
    </VuiBox>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────
function KolInsightsPage() {
  const { companyId } = useAnalytics();

  const [report, setReport]         = useState(null);
  const [loading, setLoading]       = useState(false);
  const [generating, setGenerating] = useState(false);
  const [error, setError]           = useState(null);

  const loadReport = async (cid) => {
    if (!cid) return;
    setLoading(true);
    setError(null);
    try {
      const res = await adminService.getLatestKolReport(cid);
      setReport(res.data);
    } catch (e) {
      if (e?.response?.status === 404) setReport(null);
      else setError("Failed to load the KOL report.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (companyId) loadReport(companyId); }, [companyId]);

  const handleGenerate = async () => {
    if (!companyId) return;
    setGenerating(true);
    try {
      await adminService.triggerKolReport(companyId);
      await loadReport(companyId);
    } catch {
      setError("Report generation failed. Check System Monitor for details.");
    } finally {
      setGenerating(false);
    }
  };

  const r        = report?.report;
  const coverage = report?.dataCoverage;
  const period   = coverage ? formatPeriod(coverage.windowStart, coverage.windowEnd) : "";

  // Subtitle — changes based on whether report is loaded
  const subtitle = coverage
    ? `Neurovascular physician intelligence · ${period} · ${coverage.kolsTracked} physicians · ${coverage.kolPostsCount} posts`
    : "Neurovascular physician intelligence · Quarterly briefing";

  return (
    <DashboardLayout>
      <DashboardNavbar noSelectors />
      <VuiBox px={2} py={1}>

        {/* Page header */}
        <VuiBox mb={3}>
          <VuiBox display="flex" alignItems="center" gap={1.5} mb={0.5}>
            <IoGitNetworkOutline size={22} color="#a78bfa" />
            <VuiTypography variant="h4" color="white" fontWeight="bold">
              KOL Intelligence
            </VuiTypography>
          </VuiBox>
          <VuiTypography sx={{ fontSize: "0.85rem", color: "rgba(255,255,255,0.55)", lineHeight: 1.5 }}>
            {subtitle}
          </VuiTypography>
        </VuiBox>

        {/* Loading */}
        {loading && (
          <VuiBox display="flex" justifyContent="center" py={8}>
            <CircularProgress sx={{ color: "#a78bfa" }} />
          </VuiBox>
        )}

        {/* Error */}
        {!loading && error && (
          <VuiBox sx={{ background: "rgba(239,68,68,0.08)", border: "1px solid rgba(239,68,68,0.2)", borderRadius: 2, p: 3, textAlign: "center" }}>
            <VuiTypography variant="body2" color="error">{error}</VuiTypography>
          </VuiBox>
        )}

        {/* Empty state */}
        {!loading && !error && !report && (
          <VuiBox sx={{ background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 2, p: 6, textAlign: "center" }}>
            <IoGitNetworkOutline size={40} color="rgba(255,255,255,0.15)" />
            <VuiTypography variant="h6" color="text" mt={2} mb={1}>
              No report generated yet
            </VuiTypography>
            <VuiTypography sx={{ fontSize: "0.875rem", color: "rgba(255,255,255,0.45)", mb: 3 }}>
              Run the pipeline from the admin panel, or generate a report manually below.
            </VuiTypography>
            <VuiButton
              variant="contained"
              color="info"
              onClick={handleGenerate}
              disabled={generating || !companyId}
              startIcon={generating ? <CircularProgress size={14} /> : null}
            >
              {generating ? "Generating…" : "Generate Report"}
            </VuiButton>
          </VuiBox>
        )}

        {/* Report */}
        {!loading && !error && report && r && (
          <>
            <HighlightCards highlights={r.highlights} />
            <ExecSummaryStrip bullets={r.executiveSummary} />
            <FieldOpinionCards consensus={r.consensus} divergence={r.divergence} />
            <WebPerceptionCard content={r.webPerception} />

            <Grid container spacing={0}>
              {SECTIONS.map(({ key, label, color }) => (
                <Grid item xs={12} key={key}>
                  <SectionCard label={label} content={r[key]} accentColor={color} />
                </Grid>
              ))}
            </Grid>

            {/* Brand Perception + Recommended Actions */}
            <VuiBox display="flex" gap={2} flexWrap="wrap" mb={2}>
              <VuiBox sx={{ flex: "1 1 300px" }}>
                <SectionCard label="Terumo Neuro Brand Perception" content={r.brandPerception} accentColor="#a78bfa" />
              </VuiBox>
              <RecommendedActionsCard actions={r.recommendedActions} />
            </VuiBox>

            <LockedChartsRow />

            {/* Discreet regenerate */}
            <VuiBox display="flex" justifyContent="flex-end" pt={1} sx={{ borderTop: "1px solid rgba(255,255,255,0.06)" }}>
              <VuiButton
                variant="text"
                color="secondary"
                size="small"
                onClick={handleGenerate}
                disabled={generating || !companyId}
                sx={{ fontSize: "0.75rem", color: "rgba(255,255,255,0.3)", "&:hover": { color: "rgba(255,255,255,0.55)" } }}
              >
                {generating ? "Updating…" : "Update Report"}
              </VuiButton>
            </VuiBox>
          </>
        )}
      </VuiBox>
    </DashboardLayout>
  );
}

export default KolInsightsPage;
